<script setup lang="ts">
import { formatPrice } from '@b2b-platform/commerce/utils/format';
import resolveConfig from 'tailwindcss/resolveConfig';

import { useContactRequestForm } from '~/packages/commerce/composables/useContactRequestForm';
import {
    FEATURES as PRODUCT_COMPARISON_FEATURES,
    JOB_AD_COLUMNS_LARGE_DEVICES,
    JOB_AD_COLUMNS_SMALL_DEVICES,
} from '~/packages/commerce/texts/product-comparison-job-ads';
import {
    type Product,
    ProductCategory,
} from '~/packages/commerce/types/product';
import tailwindConfig from '~/packages/manage-job-ads/tailwind.config';

const seoTitle = 'Stelleninserate schalten: Österreichs größtes Karriereportal';
const seoDescription =
    'Erreichen Sie mehr geeignete Kandidat*innen mit Österreichs größtem Karriereportal. In wenigen Klicks ist ihre Stellenanzeige sofort online!';

useSeoMeta({
    description: seoDescription,
    ogDescription: seoDescription,
    ogTitle: seoTitle,
    title: seoTitle,
});

useRobotsRule(true);

definePageMeta({
    isAuthRequired: false,
    layout: 'content',
});

const runtimeConfig = useRuntimeConfig();
const content_url = runtimeConfig.public.karriere.content.url;

const fullConfig = resolveConfig(tailwindConfig as any);
const isDesktopDevice = useMatchMedia(
    `(min-width: ${fullConfig.theme.screens.xl})`,
);

const productComparisonColumns = ref(
    isDesktopDevice.value
        ? JOB_AD_COLUMNS_LARGE_DEVICES
        : JOB_AD_COLUMNS_SMALL_DEVICES,
);

const { data: products, status } = await useLazyFetch<Product[]>(
    `/api/commerce/products/${ProductCategory.JOB_ADS}`,
);

const { open: openContactRequestForm } = useContactRequestForm();

const citations = computed<string[]>(() => {
    const performanceCitations = new Set(
        products.value
            ?.map((product) => product.features?.performance?.[0]?.description)
            .filter((value) => value) ?? [],
    );
    return [
        'Google Analytics (01/ 2024). Abgerundeter Wert von Total User auf karriere.at Seiten von Nutzer*innen mit zumindest einer Jobsuche-Intention dividiert durch 31 (Tage).',
        ...performanceCitations,
        'Google Analytics (03/2025)',
    ];
});

const getCitationIndex = (description: string) =>
    citations.value.indexOf(description) + 1;

const { data: faqItems, error: faqItemsError } = await useFetch('/api/commerce/content/faq/stelleninserate');
</script>

<template>
    <div>
        <CommBaseContentContainer as="section" class="pb-7 pt-11">
            <div class="flex flex-col gap-3">
                <BaseHeading as="h1" variant="2xl-500" class="text-left">
                    Stelleninserate schalten
                </BaseHeading>
                <BaseText>
                    Täglich suchen bis zu
                    <BaseText variant="base-500" as="span">
                        41.000 Talente
                        <CommBaseCitationLink :number="1" />
                    </BaseText>
                    aus verschiedensten Branchen und mit unterschiedlichsten
                    Qualifikationen auf karriere.at nach einem Job <br />– Ihre
                    Chance, offene Positionen gezielt zu besetzen!
                </BaseText>
            </div>
        </CommBaseContentContainer>
        <CommBaseContentContainer
            as="section"
            class="grid grid-cols-1 gap-16 py-5 md:grid-cols-3 md:gap-dynamic md:py-5 xl:grid-cols-4"
        >
            <template v-if="status === 'pending'">
                <CommProductCard
                    v-for="index in 3"
                    :key="`loading-product-card-${index}`"
                    :is-loading="true"
                    class="xl:row-start-1 xl:row-end-3 xl:grid xl:grid-rows-subgrid"
                >
                    <template #performance>
                        <div
                            :class="`rounded-t row-start-1 px-6 py-4 text-center text-sm text-white`"
                        >
                            <JamBaseSkeletonLoader
                                height="h:[16px] md:h-[16px] lg:h-[16px]"
                            />
                        </div>
                    </template>
                </CommProductCard>
            </template>
            <template v-else>
                <CommProductCard
                    v-for="product in products"
                    :key="product.id"
                    :headline="product.name"
                    :sub-headline="product.description"
                    :detail-page-url="`/produkte/stelleninserate/${product.mainProductVariantId}`"
                    can-be-purchased-online
                    :image-src="
                        product.media?.['product-illustration']?.[0]?.medium_url
                    "
                    :image-alt="
                        product.media?.['product-illustration']?.[0]?.alt
                    "
                    :features="product.features?.['teaser-list']"
                    gtm-element="Job Ad Purchase"
                >
                    <template
                        v-if="product.features?.performance?.[0]?.label"
                        #performance
                    >
                        <BaseText variant="sm-400" as="div">
                            <span
                                v-html="
                                    product.features?.performance?.[0]?.label
                                "
                            />&nbsp;
                            <CommBaseCitationLink
                                v-if="
                                    product.features?.performance?.[0]
                                        ?.description
                                "
                                :number="
                                    getCitationIndex(
                                        product.features?.performance?.[0]
                                            ?.description,
                                    )
                                "
                            />
                        </BaseText>
                    </template>
                    <template #price>
                        <div class="content-end md:min-h-[84px]">
                            <div
                                v-if="
                                    product.variants.data.find((variant) =>
                                        variant.id.includes('runtime-30'),
                                    )
                                "
                            >
                                <BaseHeading variant="base-700" as="span">
                                    {{
                                        formatPrice(
                                            product.variants.data.find(
                                                (variant) =>
                                                    variant.id.includes(
                                                        'runtime-30',
                                                    ),
                                            ).priceTiers[0].price.amount,
                                        )
                                    }}
                                </BaseHeading>
                                <BaseText variant="sm-400" as="span">
                                    nur für Neukunden und nur im Online Shop
                                    (Plus Starter)
                                </BaseText>
                            </div>
                            <div>
                                <BaseText as="span">ab</BaseText>
                                <BaseHeading
                                    variant="lg-500"
                                    as="span"
                                    class="ml-1"
                                >
                                    {{
                                        formatPrice(
                                            product.variants.data.find(
                                                (variant) =>
                                                    variant.id ===
                                                    product.mainProductVariantId,
                                            )?.priceTiers[0].price.amount,
                                        )
                                    }}
                                </BaseHeading>
                                <BaseText as="span">
                                    pro Stelleninserat
                                </BaseText>
                            </div>
                        </div>
                    </template>
                </CommProductCard>
            </template>
            <CommCardProduktberater
                class="border border-gray-200 bg-white md:col-span-3 xl:col-span-1"
                horizontal
            />
            <CommBaseCollapse
                class="col-span-1 md:col-span-3 md:row-start-2 xl:col-span-4 xl:row-start-2"
                trigger-classes="!justify-center !gap-1 xl:!w-3/4"
            >
                <template #trigger>
                    <BaseText> Vollständiger Produktvergleich </BaseText>
                </template>
                <CommBaseTable class="mt-6" :columns="productComparisonColumns">
                    <template #body>
                        <CommBaseTableSection
                            v-for="(
                                productComparisonFeature, index
                            ) in PRODUCT_COMPARISON_FEATURES"
                            :key="`comparison-section-${index}`"
                            :title="productComparisonFeature.title"
                            :rows="productComparisonFeature.rows"
                        />
                    </template>
                </CommBaseTable>
            </CommBaseCollapse>
        </CommBaseContentContainer>

        <CommBaseContentContainer>
            <section
                class="mt-16 flex flex-col items-start gap-12 rounded bg-purple-300 p-8 md:flex-row"
            >
                <div class="flex flex-col gap-4">
                    <h3 class="flex flex-col">
                        <BaseHeading as="span" variant="lg-500">
                            Stelleninserat-Service
                        </BaseHeading>
                        <BaseHeading as="span" variant="lg-400">
                            Beratung, Buchung und Erstellung
                        </BaseHeading>
                    </h3>
                    <BaseText>
                        Umfangreicher Service für Personaldienstleister,
                        Unternehmen mit großem Personalbedarf und alle, die bei
                        der Stelleninserate-Erstellung Unterstützung benötigen.
                    </BaseText>
                    <ul class="flex flex-col gap-2">
                        <CommBaseListItem
                            icon-name="checkmark-filled"
                            icon-color="gray-900"
                        >
                            Kostenloses Beratungsgespräch
                        </CommBaseListItem>
                        <CommBaseListItem
                            icon-name="checkmark-filled"
                            icon-color="gray-900"
                        >
                            Buchung, Eingabe und Verwaltung Ihres
                            Stelleninserats durch Ihre persönliche
                            Ansprechperson
                        </CommBaseListItem>
                        <CommBaseListItem
                            icon-name="checkmark-filled"
                            icon-color="gray-900"
                        >
                            Individuelle Designs gemäß Ihrer Corporate Identity
                        </CommBaseListItem>
                    </ul>
                    <BaseHeading variant="lg-500">
                        ab € 920 pro Stelleninserat
                    </BaseHeading>
                    <JamBaseButton
                        class="w-full md:w-fit md:min-w-[300px]"
                        variant="gray"
                        @click="
                            openContactRequestForm();
                            navigateTo('#contact-request');
                        "
                    >
                        Jetzt anfragen
                    </JamBaseButton>
                </div>
                <NuxtImg
                    src="/illustrations/basic-bell.svg"
                    alt=""
                    loading="lazy"
                    width="250px"
                    height="250px"
                    class="hidden max-w-[280x] self-center lg:block"
                />
            </section>
        </CommBaseContentContainer>
        <CommIncludedServicesSection
            title="Ihre Inklusivleistungen"
            class="mt-16"
        >
            <CommBaseContentContainer
                class="mt-6 hidden grid-cols-1 gap-dynamic md:grid md:grid-cols-2 xl:grid-cols-4"
            >
                <CommCardFeature
                    title="Business Portal"
                    subtitle="Im Business Portal verwalten Sie Ihre Produkte und haben die Performance Ihrer Stelleninserate im Auge."
                    image="/images/job-ads/features/business-portal/dashboard.png"
                    image-alt="Das Dashboard im Business Portal. Eingeloggt sieht man links die Navigationspunkte Übersicht, Profil, Inserate, Guthaben, Talentdatenbank, Employer Branding und Recruiting Services.
Rechts sind die drei aktuellsten Inserate inklusive restlicher Laufzeit aufgelistet. Man sieht auch das aktuelle Guthaben und aktuelle Kontingente. Am Seitenende können Online-Shop-Rechnungen eingesehen werden und es gibt eine Kontaktmöglichkeit."
                    overlay-title="Diese Funktionen erwarten Sie im Business Portal"
                    overlay-sub-title="nach erfolgreicher Onlinebuchung"
                    data-gtm-element="BP_EC: Inclusive services"
                    data-gtm-element-detail="Business Portal"
                >
                    <template #overlay>
                        <CommFeatureBusinessPortal />
                    </template>
                </CommCardFeature>

                <NuxtLink
                    target="_blank"
                    external
                    :to="`${content_url}/hr/smart-bewerben`"
                    data-gtm-element="BP_EC: Inclusive services"
                    data-gtm-element-detail="Smart bewerben"
                >
                    <CommCardFeature
                        title="smart bewerben"
                        image="/images/job-ads/features/smart-bewerben.png"
                        image-alt='Ein Stelleninserat für "Java Software Engineer*". Der smart-bewerben-Button befindet sich am rechten Rand über einer Box mit den wichtigsten Infos.'
                    >
                        <template #subtitle>
                            Mit dem smart bewerben-Button erhalten Stelleninserate im Schnitt doppelt so viele Klicks
                            auf den Bewerben-Button als ohne.
                            <CommBaseCitationLink :number="citations.length" />
                        </template>
                    </CommCardFeature>
                </NuxtLink>
                <NuxtLink
                    target="_blank"
                    external
                    :to="`${content_url}/hr/bewerbermanager`"
                >
                    <CommCardFeature
                        title="bewerber.manager"
                        image="/images/job-ads/features/bewerber-manager/bewerbermanager_1.png"
                        image-alt='Ein Stelleninserat für "Java Software Engineer*". Der smart-bewerben-Button befindet sich am rechten Rand über einer Box mit den wichtigsten Infos.'
                    >
                        <template #subtitle>
                            Das Bewerbermanagement-System von karriere.at. Ein
                            Tool für einen effizienten und gesetzeskonformen
                            Recruiting-Prozess.
                        </template>
                    </CommCardFeature>
                </NuxtLink>
                <CommCardFeature
                    title="employer.page"
                    subtitle="Das kostenfreie Arbeitgeberprofil bietet grundlegende Informationen über Ihr Unternehmen für Talente."
                    image="/images/job-ads/features/employer-page/demo-page.webp"
                    image-alt="Die kostenlose employer.page am Beispiel einer Musterfirma mit einer Standorteinbindung in Landkartendarstellung."
                    overlay-title="employer.page"
                >
                    <template #overlay>
                        <CommFeatureEmployerPage />
                    </template>
                </CommCardFeature>
            </CommBaseContentContainer>
            <CommBaseSlider class="mt-6 md:hidden">
                <CommBaseSlide>
                    <CommCardFeature
                        title="Business Portal"
                        subtitle="Im Business Portal verwalten Sie Ihre Produkte und haben die Performance Ihrer Stelleninserate im Auge."
                        image="/images/job-ads/features/business-portal/dashboard.png"
                        image-alt="Das Dashboard im Business Portal. Eingeloggt sieht man links die Navigationspunkte Übersicht, Profil, Inserate, Guthaben, Talentdatenbank, Employer Branding und Recruiting Services.
Rechts sind die drei aktuellsten Inserate inklusive restlicher Laufzeit aufgelistet. Man sieht auch das aktuelle Guthaben und aktuelle Kontingente. Am Seitenende können Online-Shop-Rechnungen eingesehen werden und es gibt eine Kontaktmöglichkeit."
                        overlay-title="Diese Funktionen erwarten Sie im Business Portal nach erfolgreicher Onlinebuchung"
                        data-gtm-element="BP_EC: Inclusive services"
                        data-gtm-element-detail="Business Portal"
                    >
                        <template #overlay>
                            <CommFeatureBusinessPortal />
                        </template>
                    </CommCardFeature>
                </CommBaseSlide>

                <CommBaseSlide>
                    <NuxtLink
                        target="_blank"
                        external
                        :to="`${content_url}/hr/smart-bewerben`"
                        data-gtm-element="BP_EC: Inclusive services"
                        data-gtm-element-detail="Smart bewerben"
                    >
                        <CommCardFeature
                            title="smart bewerben"
                            image="/images/job-ads/features/smart-bewerben.png"
                            image-alt='Ein Stelleninserat für "Java Software Engineer*". Der smart-bewerben-Button befindet sich am rechten Rand über einer Box mit den wichtigsten Infos.'
                        >
                            <template #subtitle>
                                Mit dem smart bewerben-Button erhalten
                                Stelleninserate im Schnitt doppelt so viele
                                Klicks auf den Bewerben-Button als ohne.
                                <CommBaseCitationLink
                                    :number="citations.length"
                                />
                            </template>
                        </CommCardFeature>
                    </NuxtLink>
                </CommBaseSlide>

                <CommBaseSlide>
                    <NuxtLink
                        target="_blank"
                        external
                        :to="`${content_url}/hr/bewerbermanager`"
                    >
                        <CommCardFeature
                            title="bewerber.manager"
                            image="/images/job-ads/features/bewerber-manager/bewerbermanager_1.png"
                            image-alt='Ein Stelleninserat für "Java Software Engineer*". Der smart-bewerben-Button befindet sich am rechten Rand über einer Box mit den wichtigsten Infos.'
                        >
                            <template #subtitle>
                                Das Bewerbermanagement-System von karriere.at.
                                Ein Tool für einen effizienten und
                                gesetzeskonformen Recruiting-Prozess.
                            </template>
                        </CommCardFeature>
                    </NuxtLink>
                </CommBaseSlide>
                <CommBaseSlide>
                    <CommCardFeature
                        title="employer.page"
                        subtitle="Das kostenfreie Arbeitgeberprofil bietet grundlegende Informationen über Ihr Unternehmen für Talente."
                        image="/images/job-ads/features/employer-page/demo-page.webp"
                        image-alt="Die kostenlose employer.page am Beispiel einer Musterfirma mit einer Standorteinbindung in Landkartendarstellung."
                        overlay-title="employer.page"
                        class="h-full"
                    >
                        <template #overlay>
                            <CommFeatureEmployerPage />
                        </template>
                    </CommCardFeature>
                </CommBaseSlide>
            </CommBaseSlider>
        </CommIncludedServicesSection>
        <CommBaseContentContainer>
            <CommFaqSection
                v-if="!faqItemsError"
                class="mt-14"
                :faq-items="faqItems"
            />
            <MainCitations :citations="citations" />
        </CommBaseContentContainer>
        <MainContact />
    </div>
</template>
